import { assignInlineVars } from '@vanilla-extract/dynamic';
import type { JSX } from 'react';

import {
  useDataTable,
  useDataTableContent,
} from '../../contexts/DataTableContext.js';
import * as styles from './DataTablePinningShadowCells.sty.js';

interface DataTablePinningShadowHeaderCellsProps {
  isLeftShadow: boolean;
  isRightShadow: boolean;
  leftPosition: number | undefined;
  rightPosition: number | undefined;
  gridPosition: {
    gridColumnStart: number;
    gridColumnEnd: number | `span ${number}`;
    gridRowStart: number;
    gridRowEnd: number | `span ${number}`;
  };
}

/**
 * Renders a shadow to visually emphasize pinned columns in the header.
 * To place the shadow correctly, we render a cell on top of the last left pinned/first right pinned header
 * and add the shadow inside an `::after` element to the right/left edge of this cell. The shadow is only
 * rendered when the table is actually overflowing behind the pinned columns on the respective side.
 */
export function DataTablePinningShadowHeaderCells(
  props: Readonly<DataTablePinningShadowHeaderCellsProps>,
): JSX.Element | null {
  const {
    isLeftShadow,
    isRightShadow,
    leftPosition,
    rightPosition,
    gridPosition,
  } = props;

  const tableContent = useDataTableContent();

  const table = useDataTable();

  // Check if there are pinned columns
  const hasLeftPinnedColumns = table.getStartVisibleLeafColumns().length > 0;
  const hasRightPinnedColumns = table.getEndVisibleLeafColumns().length > 0;

  // Determine whether to render left shadow
  const showLeftShadow =
    isLeftShadow && hasLeftPinnedColumns && tableContent.hasOverflowLeft;

  // Determine whether to render right shadow
  const showRightShadow =
    isRightShadow && hasRightPinnedColumns && tableContent.hasOverflowRight;

  const leftShadowCell = showLeftShadow ? (
    <div
      key="left"
      role="presentation"
      className={styles.pinningShadowCell.left}
      style={{
        ...assignInlineVars({
          [styles.leftPositionVar]: `${leftPosition}px`,
        }),
        ...gridPosition,
      }}
      data-pinned-position="left"
    />
  ) : null;

  const rightShadowCell = showRightShadow ? (
    <div
      key="right"
      role="presentation"
      className={styles.pinningShadowCell.right}
      style={{
        ...assignInlineVars({
          [styles.rightPositionVar]: `${rightPosition}px`,
        }),
        ...gridPosition,
      }}
      data-pinned-position="right"
    />
  ) : null;

  return (
    <>
      {leftShadowCell}
      {rightShadowCell}
    </>
  );
}
DataTablePinningShadowHeaderCells.displayName =
  'DataTablePinningShadowHeaderCells';
